import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Alert,
  Stack,
  Link,
  useTheme,
} from '@mui/material';
import { AppButton } from '../components/design-system';
import {
  Block as BlockIcon,
  ContactSupport as ContactIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { useSubdomain } from '../contexts/SubdomainContext';

interface SubscriptionBlockedPageProps {
  message?: string;
}

/**
 * Page shown when tenant doesn't have an active subscription
 * Blocks access to all tenant features
 */
export const SubscriptionBlockedPage: React.FC<SubscriptionBlockedPageProps> = ({ message }) => {
  const { subscriptionStatus, hasSubscription, accessReason } = useSubdomain();
  const theme = useTheme();

  const getTitle = () => {
    if (!hasSubscription) {
      return 'No Subscription Found';
    }
    
    switch (subscriptionStatus) {
      case 'canceled':
      case 'Canceled':
        return 'Subscription Canceled';
      case 'past_due':
      case 'PastDue':
        return 'Payment Past Due';
      case 'incomplete':
      case 'Incomplete':
        return 'Subscription Incomplete';
      case 'expired':
      case 'Expired':
        return 'Subscription Expired';
      case 'paused':
      case 'Paused':
        return 'Subscription Paused';
      case 'trialing':
      case 'Trialing':
        return 'Trial Ended';
      default:
        return 'Subscription Inactive';
    }
  };

  const getDescription = () => {
    if (!hasSubscription) {
      return 'Your organization does not have an active subscription. Please contact your administrator to set up billing.';
    }

    if (accessReason) {
      return accessReason;
    }
    
    switch (subscriptionStatus) {
      case 'canceled':
      case 'Canceled':
        return 'Your subscription has been canceled. Please contact your administrator to reactivate your subscription and restore access.';
      case 'past_due':
      case 'PastDue':
        return 'Your subscription payment is past due. Please update your payment method to restore access to all features.';
      case 'incomplete':
      case 'Incomplete':
        return 'Your subscription setup is incomplete. Please complete the payment setup to activate your account.';
      case 'expired':
      case 'Expired':
        return 'Your subscription has expired. Renew or contact your administrator to restore access.';
      case 'paused':
      case 'Paused':
        return 'Your subscription is paused. Contact support to resume access.';
      default:
        return message || 'Your subscription is inactive. Please contact your administrator for assistance.';
    }
  };

  const showPaymentButton =
    subscriptionStatus === 'past_due' ||
    subscriptionStatus === 'PastDue' ||
    subscriptionStatus === 'incomplete' ||
    subscriptionStatus === 'Incomplete' ||
    subscriptionStatus === 'expired' ||
    subscriptionStatus === 'Expired' ||
    hasSubscription;

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Stack spacing={3} alignItems="center" textAlign="center" py={4}>
            {/* Icon */}
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: 'error.lighter',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BlockIcon sx={{ fontSize: 60, color: 'error.main' }} />
            </Box>

            {/* Title */}
            <Typography variant="h4" component="h1" fontWeight="bold" color="error">
              {getTitle()}
            </Typography>

            {/* Description */}
            <Typography variant="body1" color="text.secondary" maxWidth="600px">
              {getDescription()}
            </Typography>

            {subscriptionStatus && (
              <Typography variant="caption" color="text.secondary">
                Subscription status: {subscriptionStatus}
              </Typography>
            )}

            {/* Alert */}
            <Alert severity="error" sx={{ width: '100%', maxWidth: '600px' }}>
              <Typography variant="body2">
                <strong>Access Restricted:</strong> All features are currently unavailable.{' '}
                {hasSubscription 
                  ? 'Your data is safe and will be accessible once your subscription is reactivated.' 
                  : 'Please contact your administrator to set up billing.'}
              </Typography>
            </Alert>

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} mt={2}>
              {showPaymentButton && (
                <AppButton
                  variant="contained"
                  size="large"
                  startIcon={<PaymentIcon />}
                  href="/billing"
                >
                  Open Billing
                </AppButton>
              )}
              <AppButton
                variant={showPaymentButton ? 'outlined' : 'contained'}
                size="large"
                startIcon={<ContactIcon />}
                href="mailto:support@cnapse.ai"
              >
                Contact Support
              </AppButton>
            </Stack>

            {/* Additional Help */}
            <Box mt={3} pt={3} borderTop={1} borderColor="divider" width="100%">
              <Typography variant="body2" color="text.secondary">
                Need help? Contact your organization's administrator or email us at{' '}
                <Link 
                  href="mailto:support@cnapse.ai"
                  underline="always"
                  sx={{
                    color: theme.palette.info.main,
                    fontWeight: 600,
                    '&:hover': {
                      color: theme.palette.info.dark,
                    },
                  }}
                >
                  support@cnapse.ai
                </Link>
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default SubscriptionBlockedPage;
